/**
 * OperatorWindow.jsx — Rê Leve
 * Janela do Operador com 5 abas + central de notificações em tempo real.
 *
 * Cada tab tem badge dinâmico que atualiza via liveSyncEngine subscribe:
 *   - Conversas Léve  → badge com # de mensagens novas desde último abrir
 *   - Conhecimento    → indicador "editado há Xmin"
 *   - Aprovações      → # de pendentes (vermelho se >0)
 *   - Operação        → # de novos pedidos do dia
 *   - Credenciais     → mostra status OpenAI se ativo
 *
 * Central de notificações renderiza toasts ao receber qualquer evento
 * relevante (ver NotificationCenter).
 */

import { useEffect, useState } from 'react';
import ConversasPanel       from './ConversasPanel';
import ConhecimentoPanel    from './ConhecimentoPanel';
import AprovacoesPanel      from './AprovacoesPanel';
import OperationsPanel      from './OperationsPanel';
import CredenciaisPanel     from './CredenciaisPanel';
import BancoDadosPanel      from './BancoDadosPanel';
import NotificationCenter   from './NotificationCenter';
import { getPendingCount }  from '../lib/pendingQuestionsEngine';
import { getChatStats }     from '../lib/chatEventsLogger';
import { subscribeAll, EVT, getSyncStatus } from '../lib/liveSyncEngine';

const TABS = [
  { id: 'conversas',    label: 'Conversas Léve',  icon: '💬', sub: 'chat em tempo real' },
  { id: 'conhecimento', label: 'Conhecimento',    icon: '✏️', sub: 'RAG · guardrails · nutrição' },
  { id: 'aprovacoes',   label: 'Aprovações',      icon: '📥', sub: 'intervenções da equipe' },
  { id: 'operacao',     label: 'Operação',        icon: '🍳', sub: 'compras · desejos · receitas' },
  { id: 'credenciais',  label: 'Credenciais',     icon: '⚙️', sub: 'chaves API + custos' },
  { id: 'banco',        label: 'Banco de Dados',  icon: '🗄', sub: 'inspeção das tabelas' },
];

function readActiveTabFromURL() {
  if (typeof window === 'undefined') return 'conversas';
  const tab = new URLSearchParams(window.location.search).get('panel');
  return TABS.some(t => t.id === tab) ? tab : 'conversas';
}

export default function OperatorWindow({ onExit }) {
  const [active, setActive] = useState(readActiveTabFromURL);
  const [tick, setTick] = useState(0);
  const [seenAt, setSeenAt] = useState(() => ({}));

  // Re-render a cada evento global
  useEffect(() => {
    const unsub = subscribeAll(() => setTick(t => t + 1));
    return () => unsub();
  }, []);

  // Marcar tab como "vista" quando fica ativa
  useEffect(() => {
    setSeenAt(prev => ({ ...prev, [active]: Date.now() }));
  }, [active]);

  function switchTab(id) {
    setActive(id);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('panel', id);
      window.history.replaceState({}, '', url);
    }
  }

  const pending = getPendingCount();
  const stats   = getChatStats();
  const syncStatus = getSyncStatus();

  // Calcular novidades por tab (eventos após o último "visto")
  function newSince(tabId) {
    const since = seenAt[tabId] || 0;
    if (tabId === 'conversas') {
      // Total de mensagens novas
      return Math.max(0, (stats.questions + stats.answers) - (seenAt[tabId + '_count'] || 0));
    }
    return 0;
  }

  function badgeFor(tabId) {
    if (tabId === 'aprovacoes') {
      return pending > 0 ? pending : null;
    }
    if (tabId === 'conversas' && tabId !== active) {
      // Não conta enquanto estou olhando
      return stats.total > 0 ? '●' : null;
    }
    return null;
  }

  return (
    <div className="rl-operator-window">
      {/* Toasts globais */}
      <NotificationCenter />

      <div className="rl-operator-tabs">
        <div className="rl-operator-tabs-inner">
          <div className="rl-operator-tabs-brand">
            <span className="rl-leve-avatar">🌱</span>
            <div>
              <div className="rl-operator-brand-title">Janela do Operador</div>
              <div className="rl-operator-brand-sub">
                Rê Leve · Recife
                <span className="rl-sync-mini" title={syncStatus.broadcastChannel ? 'BroadcastChannel ativo' : 'storage event fallback'}>
                  {syncStatus.broadcastChannel ? ' · 🟢 ao vivo' : ' · 🟡 fallback'}
                </span>
              </div>
            </div>
          </div>
          <div className="rl-operator-tabs-list" role="tablist">
            {TABS.map(t => {
              const badge = badgeFor(t.id);
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  className={'rl-operator-tab' + (active === t.id ? ' rl-operator-tab--active' : '')}
                  onClick={() => switchTab(t.id)}
                >
                  <span className="rl-operator-tab-icon">
                    {t.icon}
                    {badge !== null && (
                      <span className={'rl-operator-tab-badge' + (typeof badge === 'string' ? ' rl-operator-tab-badge--dot' : '')}>
                        {badge}
                      </span>
                    )}
                  </span>
                  <div className="rl-operator-tab-text">
                    <span className="rl-operator-tab-label">{t.label}</span>
                    <span className="rl-operator-tab-sub">{t.sub}</span>
                  </div>
                </button>
              );
            })}
          </div>
          {onExit && (
            <button className="rl-operator-exit" onClick={onExit} title="Voltar ao site do cliente">
              ← Site do cliente
            </button>
          )}
        </div>
      </div>

      <div className="rl-operator-body">
        {active === 'conversas'    && <ConversasPanel />}
        {active === 'conhecimento' && <ConhecimentoPanel />}
        {active === 'aprovacoes'   && <AprovacoesPanel />}
        {active === 'operacao'     && <OperationsPanel />}
        {active === 'credenciais'  && <CredenciaisPanel />}
        {active === 'banco'        && <BancoDadosPanel />}
      </div>
    </div>
  );
}
